import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Clock, CalendarDays, Phone } from "lucide-react";
import { BRAND } from "@/lib/site";
import { getPost, getRelatedPosts, BLOG_POSTS } from "@/lib/blog-posts";
import Seo from "@/components/site/Seo";
import Breadcrumbs from "@/components/site/Breadcrumbs";

/** Render markdown-ish body with [LINK:/path] -> <Link>. */
function renderBody(text) {
  const parts = text.split(/(\[LINK:[^\]]+\])/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[LINK:([^\]]+)\]$/);
    if (m) {
      return (
        <Link
          key={i}
          to={m[1]}
          className="text-gold-600 underline underline-offset-4 hover:text-gold-500"
        >
          learn more
        </Link>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  const related = getRelatedPosts(slug, 3);

  const path = post ? `/blog/${post.slug}` : "/blog";

  const jsonLd = useMemo(() => {
    if (!post) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      image: post.heroImage,
      datePublished: post.date,
      dateModified: post.date,
      author: {
        "@type": "Organization",
        name: BRAND.fullName,
      },
      publisher: {
        "@type": "Organization",
        name: BRAND.fullName,
        logo: {
          "@type": "ImageObject",
          url: "https://yourelitehomecare.com/downloads/logo/logo-primary-light.png",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://yourelitehomecare.com${path}`,
      },
      description: post.excerpt,
      ...(post.faqs && post.faqs.length
        ? {
            about: {
              "@type": "FAQPage",
              mainEntity: post.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          }
        : {}),
    };
  }, [post, path]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <article data-testid={`page-blog-${post.slug}`}>
      <Seo path={path} title={post.title} description={post.excerpt} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="bg-navy-900 text-white">
        <div className="container-x pt-16 pb-10 md:pt-20 md:pb-14">
          <Breadcrumbs
            dark
            crumbs={[
              { name: "Home", path: "/" },
              { name: "Resources", path: "/blog" },
              { name: post.title },
            ]}
          />
          <div className="mt-6 flex items-center gap-5 text-sm text-white/70">
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-4 w-4 text-gold-300" />
              {new Date(post.date).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-gold-300" />
              {post.readMinutes} min read
            </span>
          </div>
          <h1 className="mt-5 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl text-white">
            {post.title}
          </h1>
          <p className="mt-5 max-w-3xl text-white/80 text-lg leading-relaxed">
            {post.excerpt}
          </p>
        </div>
      </section>

      {/* HERO IMAGE */}
      <div className="container-x -mt-6 md:-mt-10 relative z-10">
        <img
          src={post.heroImage}
          alt={post.heroAlt}
          fetchPriority="high"
          className="w-full aspect-[16/9] md:aspect-[21/9] object-cover rounded-3xl shadow-2xl"
        />
      </div>

      {/* BODY */}
      <section className="section">
        <div className="container-x max-w-3xl">
          {post.sections.map((s) => (
            <div key={s.h2} className="mb-10">
              <h2 className="font-serif text-3xl md:text-4xl text-navy-900 leading-tight mb-5">
                {s.h2}
              </h2>
              <p className="text-navy-700 text-lg leading-relaxed">
                {renderBody(s.body)}
              </p>
            </div>
          ))}

          {post.faqs && post.faqs.length > 0 && (
            <div className="mt-16 pt-14 border-t border-navy-900/10">
              <h2 className="font-serif text-3xl md:text-4xl text-navy-900 leading-tight">
                Frequently asked
              </h2>
              <div className="mt-8 space-y-4">
                {post.faqs.map((f, i) => (
                  <details
                    key={f.q}
                    data-testid={`blog-faq-${i}`}
                    className="card-soft p-6 md:p-8 group [&_summary]:cursor-pointer"
                  >
                    <summary className="flex items-start justify-between gap-4 list-none">
                      <span className="font-serif text-xl text-navy-900">
                        {f.q}
                      </span>
                      <ArrowRight className="h-5 w-5 text-gold-500 mt-1 transition-transform group-open:rotate-90 shrink-0" />
                    </summary>
                    <p className="mt-4 text-navy-600 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 rounded-3xl bg-navy-900 text-white p-10 text-center">
            <h3 className="font-serif text-3xl leading-tight">
              Ready to talk about your family's care?
            </h3>
            <p className="mt-3 text-white/80 max-w-xl mx-auto">
              A free 20-minute consultation. No pressure. We'll listen first.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="btn-primary" data-testid={`blog-${post.slug}-cta`}>
                Schedule a Free Consultation
              </Link>
              <a href={BRAND.phoneHref} className="btn-ghost-light">
                <Phone className="h-4 w-4" strokeWidth={1.75} /> {BRAND.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section
        data-testid="related-posts"
        className="section bg-navy-50/50 border-t border-navy-900/5"
      >
        <div className="container-x">
          <span className="overline">Related articles</span>
          <h2 className="text-3xl md:text-4xl leading-tight">
            Related: more guidance from Hortense's team
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/blog/${r.slug}`}
                data-testid={`blog-related-${r.slug}`}
                className="card-soft overflow-hidden group"
              >
                <div className="aspect-[16/10] overflow-hidden bg-navy-100">
                  <img
                    src={r.heroImage}
                    alt={r.heroAlt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl text-navy-900 group-hover:text-gold-600 transition-colors">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-sm text-navy-600 line-clamp-2">
                    {r.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
